import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { isAdmin, listSettings, updateSetting, runScheduler } from '../api';
import HistoryTab from '../components/HistoryTab/HistoryTab';
import formStyles from '../components/EntityForm/EntityForm.module.css';
import styles from './SettingsPage.module.css';
import { useToasts } from '../hooks/useToasts';

// ---------------------------------------------------------------------------
// Section definitions — order determines display order on the page.
// Each setting entry has: key, label, type, and optional min / options.
// ---------------------------------------------------------------------------

const SECTIONS = [
  {
    id: 'company_info',
    heading: 'Company info',
    settings: [
      { key: 'company_name',    label: 'Company name',    type: 'text' },
      { key: 'company_address', label: 'Address',          type: 'text' },
      { key: 'company_website', label: 'Website',          type: 'text' },
      { key: 'company_logo',    label: 'Company logo',     type: 'logo' },
    ],
  },
  {
    id: 'scheduler',
    heading: 'Scheduler',
    settings: [
      {
        key: 'daily_scheduler_enabled',
        label: 'Daily scheduler',
        type: 'boolean',
        trueLabel: 'Enabled',
        falseLabel: 'Disabled',
      },
      {
        key: 'scheduler_days_ahead',
        label: 'Scheduler days ahead',
        type: 'number',
        min: 1,
      },
      {
        key: 'first_tee_time',
        label: 'First tee time',
        type: 'time',
      },
      {
        key: 'last_tee_time',
        label: 'Last tee time',
        type: 'time',
      },
      {
        key: 'starting_hole',
        label: 'Starting hole',
        type: 'select',
        options: [
          { value: '1', label: 'Hole 1 only' },
          { value: '1_and_10', label: 'Hole 1 and Hole 10' },
        ],
      },
      {
        key: 'gap',
        label: 'Gap between tee times (minutes)',
        type: 'number',
        min: 1,
      },
    ],
  },
  {
    id: 'booking_limits',
    heading: 'Booking limits',
    settings: [
      {
        key: 'booking_window_max_days',
        label: 'Booking window max (days)',
        type: 'number',
        min: 0,
      },
      {
        key: 'booking_window_min_days',
        label: 'Booking window min (days)',
        type: 'number',
        min: 0,
      },
      {
        key: 'max_bookings_per_day',
        label: 'Max bookings per day',
        type: 'number',
        min: 1,
      },
      {
        key: 'max_bookings_per_week',
        label: 'Max bookings per week',
        type: 'number',
        min: 1,
      },
      {
        key: 'max_bookings_per_month',
        label: 'Max bookings per month',
        type: 'number',
        min: 1,
      },
    ],
  },
  {
    id: 'player_limits',
    heading: 'Player limits',
    settings: [
      {
        key: 'max_players_per_tee_time',
        label: 'Max players per tee time',
        type: 'number',
        min: 1,
      },
      {
        key: 'max_players_per_booking',
        label: 'Max players per booking',
        type: 'number',
        min: 1,
      },
    ],
  },
  {
    id: 'golf_carts',
    heading: 'Golf carts',
    settings: [
      {
        key: 'golf_cart_capacity',
        label: 'Golf cart capacity (players per cart)',
        type: 'number',
        min: 1,
      },
      {
        key: 'allow_cross_booking_cart_sharing',
        label: 'Allow cross-booking cart sharing',
        type: 'boolean',
      },
    ],
  },
  {
    id: 'crossover',
    heading: 'Crossover',
    settings: [
      {
        key: 'nine_hole_pace',
        label: 'Nine-hole pace (minutes)',
        type: 'number',
        min: 1,
      },
    ],
  },
];

// Flat list of all setting definitions, keyed by key.
const SETTING_DEFS = {};
SECTIONS.forEach((section) => {
  section.settings.forEach((s) => {
    SETTING_DEFS[s.key] = s;
  });
});

// ---------------------------------------------------------------------------
// ToggleGroup — Yes / No pill button pair for boolean string settings
// ---------------------------------------------------------------------------

function ToggleGroup({ id, value, onChange, disabled, trueLabel = 'Yes', falseLabel = 'No' }) {
  return (
    <div className={styles.toggleGroup} role="group" aria-labelledby={`${id}-label`}>
      <button
        type="button"
        id={`${id}-yes`}
        className={`${styles.toggleBtn} ${value === 'true' ? styles.toggleBtnActive : ''}`}
        onClick={() => onChange('true')}
        disabled={disabled}
        aria-pressed={value === 'true'}
      >
        {trueLabel}
      </button>
      <button
        type="button"
        id={`${id}-no`}
        className={`${styles.toggleBtn} ${value === 'false' ? styles.toggleBtnActive : ''}`}
        onClick={() => onChange('false')}
        disabled={disabled}
        aria-pressed={value === 'false'}
      >
        {falseLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettingField — renders a single setting control with history button
// ---------------------------------------------------------------------------

function SettingField({ def, value, onChange, disabled, error, description, historyOpen, onToggleHistory }) {
  const fieldId = `setting-${def.key}`;

  const renderControl = () => {
    if (def.type === 'boolean') {
      return (
        <ToggleGroup
          id={fieldId}
          value={value}
          onChange={onChange}
          disabled={disabled}
          trueLabel={def.trueLabel}
          falseLabel={def.falseLabel}
        />
      );
    }

    if (def.type === 'select') {
      return (
        <select
          id={fieldId}
          className={`${formStyles.select} ${error ? formStyles.inputError : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {def.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (def.type === 'time') {
      return (
        <input
          id={fieldId}
          type="time"
          className={`${formStyles.input} ${error ? formStyles.inputError : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
    }

    if (def.type === 'logo') {
      return (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {value ? (
            <img
              src={value}
              alt="Company logo preview"
              style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%', background: '#ffffff', border: '1px solid #e5e5e3', flexShrink: 0 }}
            />
          ) : (
            <div
              style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0ee', flexShrink: 0 }}
              aria-label="No logo uploaded"
            />
          )}
          <input
            id={fieldId}
            type="file"
            accept="image/*"
            className={formStyles.input}
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (evt) => onChange(evt.target.result);
              reader.readAsDataURL(file);
            }}
          />
          {value && (
            <button
              type="button"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--cp-gray-500, #6b6b67)', fontSize: '13px', whiteSpace: 'nowrap' }}
              onClick={() => onChange('')}
              disabled={disabled}
            >
              Remove logo
            </button>
          )}
        </div>
      );
    }

    if (def.type === 'text') {
      return (
        <input
          id={fieldId}
          type="text"
          className={`${formStyles.input} ${error ? formStyles.inputError : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
    }

    // number
    return (
      <input
        id={fieldId}
        type="number"
        className={`${formStyles.input} ${error ? formStyles.inputError : ''}`}
        value={value}
        min={def.min !== undefined ? def.min : 0}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  };

  return (
    <div>
      <div className={formStyles.field}>
        <div className={styles.fieldLabelRow}>
          <label
            id={`${fieldId}-label`}
            className={formStyles.label}
            htmlFor={def.type === 'boolean' ? undefined : fieldId}
          >
            {def.label}
          </label>
          <button
            type="button"
            className={`${styles.historyBtn} ${historyOpen ? styles.historyBtnActive : ''}`}
            onClick={onToggleHistory}
            aria-label={`${historyOpen ? 'Hide' : 'Show'} history for ${def.label}`}
            aria-expanded={historyOpen}
          >
            <Clock size={14} strokeWidth={1.5} />
          </button>
        </div>
        {description && (
          <p className={styles.fieldDescription}>{description}</p>
        )}
        {renderControl()}
        {error && (
          <span className={styles.fieldError} role="alert">
            {error}
          </span>
        )}
      </div>
      {historyOpen && (
        <div className={styles.settingHistoryPanel}>
          <HistoryTab entityPath={`/settings/${def.key}`} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RunSchedulerCard — manual scheduler trigger, rendered inside Scheduler card
// ---------------------------------------------------------------------------

function getTodayString() {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

function getTomorrowString() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;
}

function RunSchedulerCard() {
  const [schedulerDate, setSchedulerDate] = useState(getTomorrowString);
  const [isRunning, setIsRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState('');

  const handleDateChange = (e) => {
    setSchedulerDate(e.target.value);
    setRunResult(null);
    setRunError('');
  };

  const handleRun = async () => {
    setIsRunning(true);
    setRunResult(null);
    setRunError('');
    try {
      const result = await runScheduler(schedulerDate);
      setRunResult(result);
    } catch (err) {
      setRunError(err.message || 'Scheduler run failed.');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className={styles.runSchedulerSection}>
      <span className={styles.runSchedulerLabel}>Run scheduler manually</span>
      <p className={styles.fieldDescription}>
        Select the date you want to generate tee time slots for. Already-existing slots will be skipped.
      </p>
      <div className={styles.runSchedulerRow}>
        <input
          type="date"
          className={formStyles.input}
          value={schedulerDate}
          min={getTodayString()}
          onChange={handleDateChange}
          disabled={isRunning}
          aria-label="Target date for scheduler run"
        />
        <button
          type="button"
          className={styles.runBtn}
          onClick={handleRun}
          disabled={isRunning}
        >
          {isRunning ? 'Running…' : 'Run'}
        </button>
      </div>
      {runResult && (
        <span className={styles.runSuccess}>
          Done — {runResult.created} tee time(s) created, {runResult.skipped} skipped for {runResult.target_date}.
        </span>
      )}
      {runError && (
        <span className={styles.runError} role="alert">
          {runError}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionCard — renders one group of settings in a card
// ---------------------------------------------------------------------------

function SectionCard({ section, values, descriptions, onFieldChange, disabled, fieldErrors, openHistoryKey, onToggleHistory }) {
  return (
    <div className={styles.card}>
      <p className={styles.sectionHeading}>{section.heading}</p>
      <div className={styles.fields}>
        {section.settings.map((def) => (
          <SettingField
            key={def.key}
            def={def}
            value={values[def.key] ?? ''}
            description={descriptions[def.key] || ''}
            onChange={(val) => onFieldChange(def.key, val)}
            disabled={disabled}
            error={fieldErrors[def.key] || ''}
            historyOpen={openHistoryKey === def.key}
            onToggleHistory={() => onToggleHistory(def.key)}
          />
        ))}
        {section.id === 'scheduler' && <RunSchedulerCard />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettingsPage — main page component
// ---------------------------------------------------------------------------

export default function SettingsPage({ user }) {
  const userIsAdmin = isAdmin(user);

  const originalValues = useRef({});
  const valuesRef = useRef({});

  const { toasts, addToast } = useToasts();
  const [values, setValues] = useState({});
  const [descriptions, setDescriptions] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openHistoryKey, setOpenHistoryKey] = useState(null);

  useEffect(() => {
    if (!userIsAdmin) return;

    let cancelled = false;

    setIsLoading(true);
    setLoadError('');

    listSettings()
      .then((data) => {
        if (cancelled) return;
        const map = {};
        const descMap = {};
        (data.settings || data.items || []).forEach((item) => {
          map[item.key] = item.value;
          if (item.description) descMap[item.key] = item.description;
        });
        originalValues.current = { ...map };
        valuesRef.current = map;
        setValues(map);
        setDescriptions(descMap);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message || 'Failed to load settings.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [userIsAdmin]);

  const handleFieldChange = useCallback((key, val) => {
    valuesRef.current = { ...valuesRef.current, [key]: val };
    setValues((prev) => ({ ...prev, [key]: val }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSaveError('');
  }, []);

  const handleToggleHistory = useCallback((key) => {
    setOpenHistoryKey((prev) => (prev === key ? null : key));
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError('');
    setFieldErrors({});

    const currentValues = valuesRef.current;

    const changedKeys = Object.keys(SETTING_DEFS).filter(
      (key) => currentValues[key] !== originalValues.current[key]
    );

    if (changedKeys.length === 0) {
      addToast('No changes to save.', 'success');
      return;
    }

    setIsSaving(true);

    const errors = {};
    let anyFailed = false;

    await Promise.all(
      changedKeys.map(async (key) => {
        try {
          await updateSetting(key, currentValues[key]);
          originalValues.current[key] = currentValues[key];
        } catch (err) {
          anyFailed = true;
          errors[key] = err.message || 'Failed to save.';
        }
      })
    );

    setIsSaving(false);

    if (anyFailed) {
      setFieldErrors(errors);
      setSaveError('Some settings could not be saved. See the errors below.');
    } else {
      addToast('Settings saved.');
    }
  }, [addToast]);

  if (!userIsAdmin) {
    return null;
  }

  const isBusy = isLoading || isSaving;

  return (
    <div className={styles.page}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Settings</h1>
      </div>

      {/* Load error */}
      {loadError && (
        <p className={styles.errorBanner} role="alert">
          {loadError}
        </p>
      )}

      {/* Loading state */}
      {isLoading && !loadError && (
        <p className={styles.loadingState} role="status">
          Loading settings…
        </p>
      )}

      {/* Settings sections */}
      {!isLoading && !loadError && (
        <>
          <div className={styles.sections}>
            {SECTIONS.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                values={values}
                descriptions={descriptions}
                onFieldChange={handleFieldChange}
                disabled={isBusy}
                fieldErrors={fieldErrors}
                openHistoryKey={openHistoryKey}
                onToggleHistory={handleToggleHistory}
              />
            ))}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            {saveError && (
              <p className={styles.footerError} role="alert">
                {saveError}
              </p>
            )}
            <button
              type="button"
              className={styles.saveBtn}
              onMouseDown={() => {
                if (document.activeElement instanceof HTMLInputElement) {
                  document.activeElement.blur();
                }
              }}
              onClick={handleSave}
              disabled={isBusy}
            >
              {isSaving ? 'Saving…' : 'Save all changes'}
            </button>
          </div>
        </>
      )}

      {toasts.length > 0 &&
        createPortal(
          <div className={styles.toastContainer} aria-live="polite">
            {toasts.map((t) => (
              <div
                key={t.id}
                className={[
                  styles.toast,
                  t.type === 'error' ? styles.toastError : '',
                ].join(' ').trim()}
                role="status"
              >
                {t.message}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
